import React, { useState, useEffect } from "react";
import { db, storage } from "../../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { FiSettings, FiBarChart2, FiSave, FiInfo, FiUpload, FiTrash2 } from "react-icons/fi";
import { parseMetaPixelId } from "../../utils/metaPixel";
import { resizeLogo } from "../../utils/imageUtils";
import toast from "react-hot-toast";

export default function AdminSettings() {
    const [form, setForm] = useState({
        googleAnalyticsId: "",
        metaPixelId: "",
        metaAccessToken: "",
        siteName: "LB Books",
        siteTitle: "LB Books",
        siteLogo: "",
        footerNote: "",
        footerDescription: "Bold stories for bold readers.\nBy Liliz Black from San Juan, Puerto Rico.",
        facebookUrl: "https://facebook.com/LBLilizBlack",
        instagramUrl: "https://instagram.com/lilizblack/",
        tiktokUrl: "https://tiktok.com/@liliz.black",
        goodreadsUrl: "https://www.goodreads.com/author/show/21626021.Liliz_Black",
        digitalCategory: "Digital",
        socialTitle: "Follow Liliz Black",
        authorName: "Liliz Black",
        canvasEnabled: true,
        primaryColor: "#1a6b4a",
        secondaryColor: "#0d4a35",
        accentColor: "#c8a96e",
        textColor: "#1a1a1a",
        backgroundColor: "#ffffff",
        mailerLiteApiKey: "",
        mailerLiteGroupId: "",
        categories: [],
        whatsappEnabled: false,
        whatsappNumber: "",
        whatsappMessage: "Hola! Me gustaría más información sobre tus libros."
    });
    const [newCat, setNewCat] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);

    useEffect(() => {
        async function load() {
            try {
                const [generalSnap, privateSnap] = await Promise.all([
                    getDoc(doc(db, "siteConfig", "general")),
                    getDoc(doc(db, "siteConfig", "payments_private"))
                ]);
                let generalData = {};
                let privateData = {};
                if (generalSnap.exists()) generalData = generalSnap.data();
                if (privateSnap.exists()) privateData = privateSnap.data();
                
                setForm(f => ({ 
                    ...f, 
                    ...generalData,
                    metaAccessToken: privateData.metaAccessToken || "" 
                }));
            } catch (e) {
                console.error(e);
            }
            setLoading(false);
        }
        load();
    }, []);

    function handleChange(e) {
        setForm({ ...form, [e.target.name]: e.target.value });
    }

    async function handleSave() {
        setSaving(true);
        try {
            const cleanedMetaPixelId = parseMetaPixelId(form.metaPixelId);
            
            // Separate private token from public config
            const generalForm = { ...form };
            delete generalForm.metaAccessToken;
            
            const updatedForm = {
                ...generalForm,
                metaPixelId: cleanedMetaPixelId
            };
            
            await Promise.all([
                setDoc(doc(db, "siteConfig", "general"), {
                    ...updatedForm,
                    updatedAt: serverTimestamp()
                }, { merge: true }),
                
                setDoc(doc(db, "siteConfig", "payments_private"), {
                    metaAccessToken: form.metaAccessToken || ""
                }, { merge: true })
            ]);
            
            setForm({ ...updatedForm, metaAccessToken: form.metaAccessToken });
            toast.success("Settings saved successfully!");
        } catch (e) {
            console.error(e);
            toast.error("Failed to save.");
        }
        setSaving(false);
    }

    async function handleLogoUpload(e) {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== "image/png" && file.type !== "image/svg+xml") {
            toast.error("Only PNG and SVG files are allowed for the logo.");
            return;
        }

        setUploadingLogo(true);
        const tid = toast.loading("Processing logo...");
        try {
            // Resize PNGs, pass through SVGs
            const processedFile = await resizeLogo(file, 300, 100);
            
            // Upload to storage
            const extension = file.type === "image/svg+xml" ? "svg" : "png";
            const logoRef = ref(storage, `logos/site_logo_${Date.now()}.${extension}`);
            await uploadBytes(logoRef, processedFile, { contentType: file.type });
            const url = await getDownloadURL(logoRef);

            setForm({ ...form, siteLogo: url });
            toast.success("Logo uploaded!", { id: tid });
        } catch (err) {
            console.error(err);
            toast.error(err.message || "Failed to upload logo.", { id: tid });
        }
        setUploadingLogo(false);
    }

    function addCategory() {
        if (!newCat.trim()) return;
        if (form.categories.includes(newCat.trim())) { toast.error("Category already exists."); return; }
        setForm({ ...form, categories: [...form.categories, newCat.trim()] });
        setNewCat("");
    }

    function removeCategory(cat) {
        if (form.digitalCategory === cat) {
            toast.error("Cannot remove the active Digital Category.");
            return;
        }
        setForm({ ...form, categories: form.categories.filter(c => c !== cat) });
    }

    if (loading) return <p>Loading settings...</p>;

    return (
        <div className="admin-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 30 }}>
                <div>
                    <h2 style={{ margin: 0 }}>Site Settings</h2>
                    <p style={{ color: "#888", margin: "4px 0 0", fontSize: 14 }}>Configure analytics and site-wide metadata.</p>
                </div>
                <button className="admin-btn-primary" onClick={handleSave} disabled={saving}>
                    <FiSave /> {saving ? "Saving..." : "Save Changes"}
                </button>
            </div>

            <div className="admin-card" style={{ maxWidth: 700, marginBottom: 24 }}>
                <h3 style={{ marginBottom: 12, fontSize: "1.1rem" }}>Lore Canvas</h3>
                <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                    <input type="checkbox" checked={form.canvasEnabled !== false} onChange={e => setForm(f => ({ ...f, canvasEnabled: e.target.checked }))} />
                    Show Lore Canvas on the site (menu link and page)
                </label>
                <p style={{ color: "#888", fontSize: 13, margin: "8px 0 0" }}>Turn this off to hide Canvas from visitors. Remember to click Save.</p>
            </div>

            <div className="admin-card" style={{ maxWidth: 700 }}>
                <h3 style={{ marginBottom: 20, fontSize: "1.1rem", display: "flex", alignItems: "center", gap: 10 }}>
                    <FiBarChart2 color="var(--accent)" /> Analytics & Tracking
                </h3>
                
                <div className="admin-form-field">
                    <label>Google Analytics Measurement ID</label>
                    <input 
                        name="googleAnalyticsId" 
                        value={form.googleAnalyticsId} 
                        onChange={handleChange} 
                        placeholder="G-XXXXXXXXXX" 
                    />
                    <div style={{ marginTop: 8, fontSize: 12, color: "#888", display: "flex", gap: 6, alignItems: "flex-start" }}>
                        <FiInfo style={{ flexShrink: 0, marginTop: 2 }} />
                        <span>Enter your Google Analytics 4 (GA4) Measurement ID. This will automatically enable page tracking across the storefront.</span>
                    </div>
                </div>

                <div className="admin-form-field" style={{ marginTop: 24 }}>
                    <label>Meta Pixel ID or Base Code</label>
                    <textarea 
                        name="metaPixelId" 
                        value={form.metaPixelId || ""} 
                        onChange={handleChange} 
                        placeholder="Paste your 15-16 digit Meta Pixel ID or the entire Meta Pixel base code script..." 
                        rows={3}
                        style={{ width: "100%", padding: 12, borderRadius: 8, border: "1.5px solid #eee", fontFamily: "inherit" }}
                    />
                    {form.metaPixelId && (
                        <div style={{ marginTop: 8, fontSize: 13, color: "var(--green-primary)", fontWeight: 600 }}>
                            Detected Meta Pixel ID: {parseMetaPixelId(form.metaPixelId) || "No valid Pixel ID detected"}
                        </div>
                    )}
                    <div style={{ marginTop: 8, fontSize: 12, color: "#888", display: "flex", gap: 6, alignItems: "flex-start" }}>
                        <FiInfo style={{ flexShrink: 0, marginTop: 2 }} />
                        <span>You can paste either the raw numeric Pixel ID or the full Javascript base code script snippet provided by Meta. We will automatically parse and register it.</span>
                    </div>
                </div>

                <div className="admin-form-field" style={{ marginTop: 24 }}>
                    <label>Meta Conversions API Access Token</label>
                    <textarea 
                        name="metaAccessToken" 
                        value={form.metaAccessToken || ""} 
                        onChange={handleChange} 
                        placeholder="Paste your EAa... Meta Conversions API Access Token" 
                        rows={4}
                        style={{ width: "100%", padding: 12, borderRadius: 8, border: "1.5px solid #eee", fontFamily: "inherit" }}
                    />
                    <div style={{ marginTop: 8, fontSize: 12, color: "#888", display: "flex", gap: 6, alignItems: "flex-start" }}>
                        <FiInfo style={{ flexShrink: 0, marginTop: 2 }} />
                        <span>This token allows our backend Cloud Functions to fire Conversions API event payloads directly to Meta. It will be stored securely in your private payment configurations.</span>
                    </div>
                </div>

                <hr style={{ margin: "30px 0", border: 0, borderTop: "1px solid #eee" }} />

                <h3 style={{ marginBottom: 20, fontSize: "1.1rem", display: "flex", alignItems: "center", gap: 10 }}>
                    <FiSettings color="var(--accent)" /> General Info
                </h3>

                <div className="admin-form-field">
                    <label>Website Name</label>
                    <input 
                        name="siteName" 
                        value={form.siteName} 
                        onChange={handleChange} 
                        placeholder="LB Books" 
                    />
                    <p style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Used in Navbar, Footer, and Emails.</p>
                </div>

                <div className="admin-form-field">
                    <label>Website Logo</label>
                    <div style={{ display: "flex", gap: 20, alignItems: "center", background: "#fcfcfc", padding: 20, borderRadius: 12, border: "1.5px dashed #eee" }}>
                        <div style={{ width: 100, height: 60, borderRadius: 8, border: "1.5px solid #eee", overflow: "hidden", background: "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            {form.siteLogo ? (
                                <img src={form.siteLogo} alt="Logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                            ) : (
                                <span style={{ fontSize: 10, color: "#aaa" }}>No Logo</span>
                            )}
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", gap: 10 }}>
                                <label className="admin-btn-secondary" style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                                    <FiUpload /> {uploadingLogo ? "Uploading..." : "Upload Logo"}
                                    <input type="file" accept="image/png, image/svg+xml" hidden onChange={handleLogoUpload} disabled={uploadingLogo} />
                                </label>
                                {form.siteLogo && (
                                    <button className="admin-btn-danger" onClick={() => setForm({...form, siteLogo: ""})} style={{ padding: "8px 12px" }}>
                                        <FiTrash2 />
                                    </button>
                                )}
                            </div>
                            <p style={{ fontSize: 11, color: "#888", marginTop: 8 }}>
                                <strong>Requirement:</strong> PNG or SVG files. <br/>
                                <strong>Tip:</strong> SVGs recommended for best clarity. PNGs are optimized for high-DPI screens.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="admin-form-field">
                    <label>Logo URL (Manual Override)</label>
                    <input 
                        name="siteLogo" 
                        value={form.siteLogo} 
                        onChange={handleChange} 
                        placeholder="https://example.com/logo.png" 
                    />
                </div>

                <div className="admin-form-field">
                    <label>SEO Site Title</label>
                    <input 
                        name="siteTitle" 
                        value={form.siteTitle} 
                        onChange={handleChange} 
                        placeholder="LB Books | Historias Audaces" 
                    />
                    <p style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Used for browser tab title and search results.</p>
                </div>

                <div className="admin-form-field">
                    <label>Author Name</label>
                    <input 
                        name="authorName" 
                        value={form.authorName} 
                        onChange={handleChange} 
                        placeholder="Liliz Black" 
                    />
                    <p style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Used in the blog section header (e.g. "Author's worlds").</p>
                </div>

                <div className="admin-form-field">
                    <label>Footer Copyright Note</label>
                    <input 
                        name="footerNote" 
                        value={form.footerNote} 
                        onChange={handleChange} 
                        placeholder="© 2024 LB Books. All rights reserved." 
                    />
                </div>

                <hr style={{ margin: "30px 0", border: 0, borderTop: "1px solid #eee" }} />

                <h3 style={{ marginBottom: 20, fontSize: "1.1rem", display: "flex", alignItems: "center", gap: 10 }}>
                    <FiSettings color="var(--accent)" /> Product Categories
                </h3>

                <div className="admin-form-field">
                    <label>Manage Store Categories</label>
                    <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                        <input 
                            value={newCat} 
                            onChange={e => setNewCat(e.target.value)} 
                            placeholder="e.g. Science Fiction" 
                            style={{ flex: 1 }}
                        />
                        <button className="admin-btn-secondary" onClick={addCategory} style={{ padding: "0 20px" }}>Add</button>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {form.categories.map(cat => (
                            <div key={cat} style={{ background: "#f8f9fa", padding: "6px 12px", borderRadius: 20, fontSize: 13, display: "flex", alignItems: "center", gap: 8, border: "1.5px solid #eee" }}>
                                <span>{cat}</span>
                                <button onClick={() => removeCategory(cat)} style={{ background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 14 }}>&times;</button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="admin-form-field" style={{ marginTop: 20 }}>
                    <label>Digital Identifier Category</label>
                    <p style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>Products in this category will be automatically marked as digital items (e-books).</p>
                    <select 
                        name="digitalCategory" 
                        value={form.digitalCategory} 
                        onChange={handleChange}
                        style={{ width: "100%", padding: 12, borderRadius: 8, border: "1.5px solid #eee" }}
                    >
                        <option value="">None</option>
                        {form.categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>

                <hr style={{ margin: "30px 0", border: 0, borderTop: "1px solid #eee" }} />

                <h3 style={{ marginBottom: 20, fontSize: "1.1rem", display: "flex", alignItems: "center", gap: 10 }}>
                    <FiSettings color="var(--accent)" /> Theme Colors
                </h3>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 25 }}>
                    <div className="admin-form-field">
                        <label>Primary Color</label>
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <div style={{ 
                                position: "relative", 
                                minWidth: 48, 
                                height: 48, 
                                borderRadius: 12, 
                                border: "3px solid #f0f0f0", 
                                background: form.primaryColor || "#1a6b4a",
                                cursor: "pointer",
                                boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
                            }}>
                                <input 
                                    type="color" 
                                    name="primaryColor" 
                                    value={form.primaryColor || "#1a6b4a"} 
                                    onChange={handleChange} 
                                    style={{ 
                                        position: "absolute", 
                                        top: 0, 
                                        left: 0, 
                                        width: "100%", 
                                        height: "100%", 
                                        opacity: 0, 
                                        cursor: "pointer",
                                        padding: 0,
                                        border: "none"
                                    }} 
                                />
                            </div>
                            <input 
                                name="primaryColor" 
                                value={form.primaryColor} 
                                onChange={handleChange} 
                                placeholder="#1a6b4a" 
                                style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1.5px solid #eee" }} 
                            />
                        </div>
                    </div>

                    <div className="admin-form-field">
                        <label>Secondary Color</label>
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <div style={{ 
                                position: "relative", 
                                minWidth: 48, 
                                height: 48, 
                                borderRadius: 12, 
                                border: "3px solid #f0f0f0", 
                                background: form.secondaryColor || "#0d4a35",
                                cursor: "pointer",
                                boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
                            }}>
                                <input 
                                    type="color" 
                                    name="secondaryColor" 
                                    value={form.secondaryColor || "#0d4a35"} 
                                    onChange={handleChange} 
                                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }} 
                                />
                            </div>
                            <input 
                                name="secondaryColor" 
                                value={form.secondaryColor} 
                                onChange={handleChange} 
                                placeholder="#0d4a35" 
                                style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1.5px solid #eee" }} 
                            />
                        </div>
                    </div>

                    <div className="admin-form-field">
                        <label>Accent Color</label>
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <div style={{ 
                                position: "relative", 
                                minWidth: 48, 
                                height: 48, 
                                borderRadius: 12, 
                                border: "3px solid #f0f0f0", 
                                background: form.accentColor || "#c8a96e",
                                cursor: "pointer",
                                boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
                            }}>
                                <input 
                                    type="color" 
                                    name="accentColor" 
                                    value={form.accentColor || "#c8a96e"} 
                                    onChange={handleChange} 
                                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }} 
                                />
                            </div>
                            <input 
                                name="accentColor" 
                                value={form.accentColor} 
                                onChange={handleChange} 
                                placeholder="#c8a96e" 
                                style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1.5px solid #eee" }} 
                            />
                        </div>
                    </div>

                    <div className="admin-form-field">
                        <label>Text Color</label>
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <div style={{ 
                                position: "relative", 
                                minWidth: 48, 
                                height: 48, 
                                borderRadius: 12, 
                                border: "3px solid #f0f0f0", 
                                background: form.textColor || "#1a1a1a",
                                cursor: "pointer",
                                boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
                            }}>
                                <input 
                                    type="color" 
                                    name="textColor" 
                                    value={form.textColor || "#1a1a1a"} 
                                    onChange={handleChange} 
                                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }} 
                                />
                            </div>
                            <input 
                                name="textColor" 
                                value={form.textColor} 
                                onChange={handleChange} 
                                placeholder="#1a1a1a" 
                                style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1.5px solid #eee" }} 
                            />
                        </div>
                    </div>

                    <div className="admin-form-field">
                        <label>Global Background</label>
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <div style={{ 
                                position: "relative", 
                                minWidth: 48, 
                                height: 48, 
                                borderRadius: 12, 
                                border: "3px solid #f0f0f0", 
                                background: form.backgroundColor || "#ffffff",
                                cursor: "pointer",
                                boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
                            }}>
                                <input 
                                    type="color" 
                                    name="backgroundColor" 
                                    value={form.backgroundColor || "#ffffff"} 
                                    onChange={handleChange} 
                                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }} 
                                />
                            </div>
                            <input 
                                name="backgroundColor" 
                                value={form.backgroundColor} 
                                onChange={handleChange} 
                                placeholder="#ffffff" 
                                style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1.5px solid #eee" }} 
                            />
                        </div>
                    </div>

                    <div className="admin-form-field">
                        <label>Featured Section BG</label>
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <div style={{ 
                                position: "relative", 
                                minWidth: 48, 
                                height: 48, 
                                borderRadius: 12, 
                                border: "3px solid #f0f0f0", 
                                background: form.featuredBg || "#0a0a0a",
                                cursor: "pointer",
                                boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
                            }}>
                                <input 
                                    type="color" 
                                    name="featuredBg" 
                                    value={form.featuredBg || "#0a0a0a"} 
                                    onChange={handleChange} 
                                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }} 
                                />
                            </div>
                            <input 
                                name="featuredBg" 
                                value={form.featuredBg} 
                                onChange={handleChange} 
                                placeholder="#0a0a0a" 
                                style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1.5px solid #eee" }} 
                            />
                        </div>
                    </div>

                    <div className="admin-form-field">
                        <label>Featured Section Text</label>
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <div style={{ 
                                position: "relative", 
                                minWidth: 48, 
                                height: 48, 
                                borderRadius: 12, 
                                border: "3px solid #f0f0f0", 
                                background: form.featuredText || "#ffffff",
                                cursor: "pointer",
                                boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
                            }}>
                                <input 
                                    type="color" 
                                    name="featuredText" 
                                    value={form.featuredText || "#ffffff"} 
                                    onChange={handleChange} 
                                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }} 
                                />
                            </div>
                            <input 
                                name="featuredText" 
                                value={form.featuredText} 
                                onChange={handleChange} 
                                placeholder="#ffffff" 
                                style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1.5px solid #eee" }} 
                            />
                        </div>
                    </div>

                    <div className="admin-form-field">
                        <label>Featured Accent/Button</label>
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <div style={{ 
                                position: "relative", 
                                minWidth: 48, 
                                height: 48, 
                                borderRadius: 12, 
                                border: "3px solid #f0f0f0", 
                                background: form.featuredAccent || "#c8a96e",
                                cursor: "pointer",
                                boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
                            }}>
                                <input 
                                    type="color" 
                                    name="featuredAccent" 
                                    value={form.featuredAccent || "#c8a96e"} 
                                    onChange={handleChange} 
                                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }} 
                                />
                            </div>
                            <input 
                                name="featuredAccent" 
                                value={form.featuredAccent} 
                                onChange={handleChange} 
                                placeholder="#c8a96e" 
                                style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1.5px solid #eee" }} 
                            />
                        </div>
                    </div>

                    <div className="admin-form-field">
                        <label>Featured Button Text</label>
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <div style={{ 
                                position: "relative", 
                                minWidth: 48, 
                                height: 48, 
                                borderRadius: 12, 
                                border: "3px solid #f0f0f0", 
                                background: form.featuredBtnText || "#ffffff",
                                cursor: "pointer",
                                boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
                            }}>
                                <input 
                                    type="color" 
                                    name="featuredBtnText" 
                                    value={form.featuredBtnText || "#ffffff"} 
                                    onChange={handleChange} 
                                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }} 
                                />
                            </div>
                            <input 
                                name="featuredBtnText" 
                                value={form.featuredBtnText} 
                                onChange={handleChange} 
                                placeholder="#ffffff" 
                                style={{ flex: 1, padding: "12px", borderRadius: 10, border: "1.5px solid #eee" }} 
                            />
                        </div>
                    </div>
                </div>

                <hr style={{ margin: "30px 0", border: 0, borderTop: "1px solid #eee" }} />

                <h3 style={{ marginBottom: 20, fontSize: "1.1rem", display: "flex", alignItems: "center", gap: 10 }}>
                    <FiSettings color="var(--accent)" /> Footer Content
                </h3>

                <div className="admin-form-field">
                    <label>Footer Description</label>
                    <textarea 
                        name="footerDescription" 
                        value={form.footerDescription} 
                        onChange={handleChange} 
                        placeholder="Bold stories for bold readers..." 
                        rows={3}
                        style={{ width: "100%", padding: 12, borderRadius: 8, border: "1.5px solid #eee", fontFamily: "inherit" }}
                    />
                </div>

                <div className="admin-form-field">
                    <label>Facebook URL</label>
                    <input name="facebookUrl" value={form.facebookUrl} onChange={handleChange} placeholder="https://facebook.com/..." />
                </div>

                <div className="admin-form-field">
                    <label>Instagram URL</label>
                    <input name="instagramUrl" value={form.instagramUrl} onChange={handleChange} placeholder="https://instagram.com/..." />
                </div>

                <div className="admin-form-field">
                    <label>TikTok URL</label>
                    <input name="tiktokUrl" value={form.tiktokUrl} onChange={handleChange} placeholder="https://tiktok.com/@..." />
                </div>
                
                <div className="admin-form-field">
                    <label>Goodreads URL</label>
                    <input name="goodreadsUrl" value={form.goodreadsUrl} onChange={handleChange} placeholder="https://www.goodreads.com/author/..." />
                </div>

                <div className="admin-form-field">
                    <label>Social Section Title</label>
                    <input 
                        name="socialTitle" 
                        value={form.socialTitle} 
                        onChange={handleChange} 
                        placeholder="Follow Liliz Black" 
                    />
                    <p style={{ fontSize: 11, color: "#888", marginTop: 4 }}>The heading for your social media links (e.g. FOLLOW ME).</p>
                </div>

                <div className="admin-form-field">
                    <label>Footer Copyright Note</label>
                    <input 
                        name="footerNote" 
                        value={form.footerNote} 
                        onChange={handleChange} 
                        placeholder="© 2024 LB Books. All rights reserved." 
                    />
                    <p style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Leave empty to use default copyright text.</p>
                </div>

                <hr style={{ margin: "30px 0", border: 0, borderTop: "1px solid #eee" }} />

                <h3 style={{ marginBottom: 20, fontSize: "1.1rem", display: "flex", alignItems: "center", gap: 10 }}>
                    <FiSettings color="var(--accent)" /> Newsletter (MailerLite)
                </h3>

                <div className="admin-form-field">
                    <label>MailerLite API Key</label>
                    <input 
                        name="mailerLiteApiKey" 
                        type="password"
                        value={form.mailerLiteApiKey} 
                        onChange={handleChange} 
                        placeholder="Paste your API key here" 
                    />
                    <p style={{ fontSize: 12, color: "#888", marginTop: 4 }}>Found in MailerLite: Integrations → API.</p>
                </div>

                <div className="admin-form-field">
                    <label>MailerLite Group ID</label>
                    <input 
                        name="mailerLiteGroupId" 
                        value={form.mailerLiteGroupId} 
                        onChange={handleChange} 
                        placeholder="e.g. 112233445" 
                    />
                    <p style={{ fontSize: 12, color: "#888", marginTop: 4 }}>The ID of the group/list where subscribers should be added.</p>
                </div>

                <hr style={{ margin: "30px 0", border: 0, borderTop: "1px solid #eee" }} />

                <h3 style={{ marginBottom: 20, fontSize: "1.1rem", display: "flex", alignItems: "center", gap: 10 }}>
                    <FiSettings color="var(--accent)" /> WhatsApp Floating Bubble
                </h3>

                <div className="admin-form-field">
                    <div style={{ display: "flex", alignItems: "center", gap: 12, background: "#f8f9fa", padding: 16, borderRadius: 12, border: "1.5px solid #eee" }}>
                        <label className="toggle-switch">
                            <input 
                                type="checkbox" 
                                name="whatsappEnabled" 
                                checked={form.whatsappEnabled} 
                                onChange={e => setForm({...form, whatsappEnabled: e.target.checked})} 
                            />
                            <span className="toggle-slider"></span>
                        </label>
                        <span style={{ fontWeight: 700, fontSize: 14 }}>Enable WhatsApp Floating Bubble</span>
                    </div>
                </div>

                {form.whatsappEnabled && (
                    <div style={{ animation: "fadeIn 0.3s ease", display: "grid", gap: 20, padding: 20, background: "#fdfdfd", border: "1.5px solid #eee", borderRadius: 12 }}>
                        <div className="admin-form-field">
                            <label>WhatsApp Phone Number</label>
                            <input 
                                name="whatsappNumber" 
                                value={form.whatsappNumber} 
                                onChange={handleChange} 
                                placeholder="e.g. 17871234567" 
                            />
                            <p style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Include country code without symbols (e.g., 1 for USA/PR + number).</p>
                        </div>
                        <div className="admin-form-field">
                            <label>Default Message</label>
                            <input 
                                name="whatsappMessage" 
                                value={form.whatsappMessage} 
                                onChange={handleChange} 
                                placeholder="Hola! Me gustaría más información..." 
                            />
                            <p style={{ fontSize: 11, color: "#888", marginTop: 4 }}>The pre-filled text when a user starts a chat.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
